import { useEffect, useRef, useState } from 'react'

/**
 * Smoothly animates a number between its previous value and a new
 * `target` whenever the target changes. Eases out (cubic) so the
 * change feels like a natural countdown / countup rather than a hard
 * jump.
 *
 * Differs from `useCountUp` (which always animates 0 → target on
 * mount/dep-change): this hook tracks the *currently displayed* value
 * via a ref and tweens from there, so mid-tween target changes
 * don't snap.
 *
 * Use cases:
 *   - Player balance (count down on bet placed, up on win)
 *   - Total stake (count up as chips are added, down as they're cleared)
 *   - Potential win (tracks both placement and removal)
 *
 * Initial render sets the value to `target` immediately (no animation
 * on first render — we don't know what the "previous" value should be).
 */
export function useAnimatedNumber(target: number, durationMs = 600): number {
  const [value, setValue] = useState(target)
  // Mirror the displayed value into a ref so the next tween starts from
  // wherever we currently are, even if a previous tween hasn't finished.
  const valueRef = useRef(target)
  valueRef.current = value
  const targetRef = useRef(target)

  useEffect(() => {
    if (!Number.isFinite(target)) {
      setValue(target)
      targetRef.current = target
      return
    }
    if (targetRef.current === target) return
    targetRef.current = target

    const start = valueRef.current
    if (start === target) return

    let raf = 0
    let startedAt: number | null = null

    const tick = (now: number) => {
      if (startedAt === null) startedAt = now
      const elapsed = now - startedAt
      const progress = Math.min(1, elapsed / durationMs)
      // ease-out cubic: fast initial movement, gentle settle.
      const eased = 1 - Math.pow(1 - progress, 3)
      const current = start + (target - start) * eased
      setValue(current)
      if (progress < 1) {
        raf = requestAnimationFrame(tick)
      }
    }

    raf = requestAnimationFrame(tick)

    return () => {
      if (raf) cancelAnimationFrame(raf)
    }
  }, [target, durationMs])

  return value
}
